import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, XCircle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const STATUS = { trialing: "Prova gratuita in corso", active: "Attivo", past_due: "Pagamento in sospeso", canceled: "Disdetto", incomplete: "Da completare", unpaid: "Non pagato" };
const fmt = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "-");

export function SubscriptionCard() {
  const [sub, setSub] = useState(null);
  const load = () => api.get("/billing/subscription").then((r) => setSub(r.data));
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    try {
      const r = await api.post("/billing/cancel");
      toast.success(`Abbonamento disdetto. Resta attivo fino al ${fmt(r.data.endsAt)}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disdetta non riuscita"); }
  };

  const portal = async () => {
    try { const r = await api.post("/billing/portal"); window.location.href = r.data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };

  if (!sub || !sub.required) return null;
  const has = !!sub.stripeSubscriptionId;
  const ended = sub.status === "canceled";

  return (
    <div className="mt-8 card-premium p-6" data-testid="subscription-card">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2"><CreditCard className="w-5 h-5 text-gold" /><h3 className="font-display text-xl font-bold">Il tuo abbonamento</h3></div>
          {!sub.paymentsEnabled && !has && <p className="text-sm text-slate-600" data-testid="subscription-pending">Pagamenti online in attivazione: {["artist","supplier"].includes(sub.role) ? "il tuo profilo è già attivo e visibile" : "il tuo account è già attivo"}. Ti avviseremo quando potrai attivare la prova di 7 giorni.</p>}
          {sub.paymentsEnabled && !has && sub.legacyActive && <p className="text-sm text-slate-600">Account attivo senza abbonamento collegato.</p>}
          {sub.paymentsEnabled && !has && !sub.legacyActive && <p className="text-sm text-slate-600" data-testid="subscription-missing">Per comparire nel portale attiva la prova gratuita di 7 giorni (serve la carta).</p>}
          {has && (
            <div className="text-sm text-slate-700 space-y-1" data-testid="subscription-details">
              <div>Stato: <strong data-testid="subscription-status">{sub.cancelAtPeriodEnd && !ended ? "Disdetto (attivo fino a scadenza)" : STATUS[sub.status] || sub.status}</strong></div>
              <div>Piano: <strong data-testid="subscription-plan">{sub.plan === "standard" ? "Standard · 19,90€/anno" : "Professional · 58,80€/anno"}</strong></div>
              {sub.status === "trialing" && <div>Fine prova: <strong>{fmt(sub.trialEnd)}</strong> · poi 1€ per i primi 2 mesi</div>}
              {sub.status !== "trialing" && !ended && <div>{sub.cancelAtPeriodEnd ? "Termina il" : "Prossimo rinnovo"}: <strong>{fmt(sub.currentPeriodEnd)}</strong></div>}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
        <Link to="/piani" data-testid="subscription-compare" className="btn-pill-outline">{has ? "Cambia piano" : "Confronta i piani"}</Link>
        {has && !ended && <button data-testid="subscription-portal" onClick={portal} className="btn-pill-outline"><CreditCard className="w-4 h-4" />Cambia metodo di pagamento</button>}
        {sub.paymentsEnabled && (!has || ended) && !sub.legacyActive && <Link to="/abbonamento" data-testid="subscription-start" className="btn-pill-gold">Attiva prova gratuita</Link>}
        {has && !ended && !sub.cancelAtPeriodEnd && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="subscription-cancel-btn" className="btn-pill-outline !border-red-300 !text-red-600"><XCircle className="w-4 h-4" />Disdici abbonamento</button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Vuoi disdire l'abbonamento?</AlertDialogTitle>
                <AlertDialogDescription>Non ti verrà addebitato più nulla. Il profilo resta visibile fino alla fine del periodo già pagato (o della prova), poi verrà nascosto dal portale.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="subscription-cancel-dismiss">Annulla</AlertDialogCancel>
                <AlertDialogAction data-testid="subscription-cancel-confirm" onClick={cancel} className="bg-red-600 hover:bg-red-700">Conferma disdetta</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        </div>
      </div>
    </div>
  );
}
